// [ai] /api/events (SSE, app/lib/events.py) → the client. One EventSource per tab (the browser reconnects it); an `invalidate`
// event refetches exactly the named queries something on the page holds (TanStack marks the rest stale, no request); a repeated
// event (same type + at) collapses; the connection state is what the shell shows. Add a server event type to EVENT_TYPES —
// SSE names its events, and a listener exists only for the names listed here.
import type { QueryClient } from "@tanstack/react-query";

export const EVENT_TYPES = ["invalidate", "transcribe", "ingest", "import"] as const;
export type ServerEvent = { type: string; at: number; queries?: string[] } & Record<string, unknown>;
export type EventHandlers = { onState: (s: { connected: boolean }) => void; onEvent?: (ev: ServerEvent) => void };

export function connectEvents(client: QueryClient, h: EventHandlers): () => void {
  const es = new EventSource("/api/events"); let last = "";
  es.onopen = () => h.onState({ connected: true });
  es.onerror = () => h.onState({ connected: false });   // the browser retries on its own; the shell shows the gap
  const handle = (e: MessageEvent) => {
    let ev: ServerEvent;
    try { ev = JSON.parse(String(e.data)) as ServerEvent; } catch { return; }
    if (!ev || typeof ev !== "object" || typeof ev.type !== "string" || typeof ev.at !== "number") return;
    const key = String(e.data); if (key === last) return; last = key;   // the whole payload: two publishes in one millisecond with different queries are two events
    if (ev.type === "invalidate") for (const name of ev.queries ?? []) void client.invalidateQueries({ queryKey: ["q", name] });
    h.onEvent?.(ev);
  };
  for (const t of EVENT_TYPES) es.addEventListener(t, handle);
  return () => es.close();
}
